import type { PartnerEnum } from '../groveSchedulerClient'
import { cloud9Schema } from './cloud_9.schema'
import { curveHeroSchema } from './curve_hero.schema'
import { dentallySchema } from './dentally.schema'
import { denticonSchema } from './denticon.schema'
import { dentrixAscendSchema } from './dentrix_ascend.schema'
import { dentrixCoreSchema } from './dentrix_core.schema'
import { eyefinitySchema } from './eyefinity.schema'
import { greyfinchSchema } from './greyfinch.schema'
import { janeSchema } from './jane.schema'
import { kollaSchema } from './kolla.schema'
import { maxassistSchema } from './maxassist.schema'
import { openDentalSchema } from './open_dental.schema'
import { trackerSchema } from './tracker.schema'
import type { PartnerSchemaInternal } from './types'

/**
 * Central registry of all partner configuration schemas (internal variant)
 * Contains full schema with all internal fields for admin3/employee use
 * Maps partner identifiers to their field schemas
 */
export const PARTNER_SCHEMAS_INTERNAL: Record<string, PartnerSchemaInternal> = {
  cloud_9: cloud9Schema,
  curve_hero: curveHeroSchema,
  dentrix_ascend: dentrixAscendSchema,
  dentrix_core: dentrixCoreSchema,
  eyefinity: eyefinitySchema,
  dentally: dentallySchema,
  denticon: denticonSchema,
  greyfinch: greyfinchSchema,
  jane: janeSchema,
  kolla: kollaSchema,
  maxassist: maxassistSchema,
  open_dental: openDentalSchema,
  tracker: trackerSchema,
}

/**
 * Get the internal configuration schema for a specific partner
 * Returns the full schema with all internal fields
 *
 * @param partner - Partner identifier
 * @returns Partner schema if defined, null otherwise
 */
export const getSchemaForPartner = (
  partner: PartnerEnum | string,
): PartnerSchemaInternal | null => {
  return PARTNER_SCHEMAS_INTERNAL[partner] || null
}

/**
 * Check if a partner has form mode support
 *
 * @param partner - Partner identifier
 * @returns true if partner has a defined schema, false otherwise
 */
export const hasFormSupport = (partner: PartnerEnum | string): boolean => {
  return partner in PARTNER_SCHEMAS_INTERNAL
}

/**
 * Get OAuth URL for a partner's authorization flow
 * Returns null if partner doesn't require OAuth
 *
 * @param partner - Partner identifier
 * @param agentId - Agent ID for authorization
 * @returns OAuth URL or null if not applicable
 */
export const getOAuthUrlForPartner = (
  partner: string,
  agentId: string,
): string | null => {
  const schema = getSchemaForPartner(partner)
  if (!schema?.buildOAuthUrl) return null
  return schema.buildOAuthUrl(agentId)
}

// Re-export types for convenience
export * from './types'
